/**
 * Renewals shared types — Story R1 (Renewals Phase 1: domain + settings).
 *
 * String-union types for bucket + threshold-status values.
 * No magic values — import these types, never inline the strings.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §2 (Renewals Phase 1)]
 * [Source: docs/architecture/coding-standards.md#1-cross-cutting-conventions-all-languages]
 */

// ── Bucket type ───────────────────────────────────────────────────────────────

/**
 * RenewalBucket — which time-band the policy falls into, relative to its actByDate.
 *
 * Ordered evaluation (first match wins):
 *   daysUntilActBy < 0         → 'overdue'   (actByDate already passed)
 *   daysUntilActBy ≤ 14        → 'le_14d'
 *   daysUntilActBy ≤ 30        → 'le_30d'
 *   daysUntilActBy ≤ 90        → 'le_3mo'    (≤ 3 months)
 *   daysUntilActBy ≤ 180       → 'le_6mo'    (≤ 6 months)
 *   daysUntilActBy > 180       → 'gt_6mo'
 *   actByDate == null          → 'unknown'   (missing renewalDate or noticePeriodDays)
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §2 — band table]
 */
export type RenewalBucket =
  | 'le_14d'   // ≤ 14 days until actByDate
  | 'le_30d'   // ≤ 30 days
  | 'le_3mo'   // ≤ 90 days (≤ 3 months)
  | 'le_6mo'   // ≤ 180 days (≤ 6 months)
  | 'gt_6mo'   // > 6 months
  | 'overdue'  // actByDate already passed (daysUntilActBy < 0)
  | 'unknown'; // actByDate could not be computed (missing renewalDate or noticePeriodDays)

// ── Threshold status type ─────────────────────────────────────────────────────

/**
 * RenewalThresholdStatus — alert colour based on proximity to actByDate.
 *
 * "Red wins": mahnen is checked first (red window ⊆ orange window).
 *   daysUntilActBy ≤ redDays   → 'mahnen'        (red; also when overdue, i.e. d<0)
 *   daysUntilActBy ≤ orangeDays → 'informieren'   (orange)
 *   actByDate == null          → 'none'
 *   otherwise                  → 'none'
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §2 — "red wins"]
 */
export type RenewalThresholdStatus =
  | 'none'         // outside both windows (or actByDate unknown)
  | 'informieren'  // orange — within N1 days of actByDate
  | 'mahnen';      // red — within N2 days of actByDate (red wins)

// ── Bucket boundary constants ─────────────────────────────────────────────────

/**
 * Canonical day-count boundaries for RenewalBucket classification.
 *
 * Used by both the domain (renewals-math.ts) and the UI (band labels).
 * Never inline these numbers — import this constant.
 *
 * [Source: Story R1 spec §3 "Bucket boundary semantics"]
 */
export const BUCKET_BOUNDARY_DAYS = {
  le_14d: 14,
  le_30d: 30,
  le_3mo: 90,
  le_6mo: 180,
} as const;

// ── R2: Signal status, board/heute DTOs, and bucket tuple ────────────────────

/**
 * RenewalSignalStatus — the statuses that can be persisted in renewal_signal.
 * 'none' is never persisted (it means "no signal"). Only active alerts are stored.
 *
 * [Source: Story R2 spec §3]
 */
export type RenewalSignalStatus = Exclude<RenewalThresholdStatus, 'none'>; // 'informieren' | 'mahnen'

/**
 * Ordered tuple of all RenewalBucket values.
 * Use this to zero-initialise a bucketCounts record and preserve consistent display order.
 * Never inline these strings — import this constant.
 *
 * [Source: Story R2 spec §3]
 */
export const RENEWAL_BUCKETS: readonly RenewalBucket[] = [
  'overdue',
  'le_14d',
  'le_30d',
  'le_3mo',
  'le_6mo',
  'gt_6mo',
  'unknown',
] as const;

/**
 * One board row: an active policy + its renewal assessment + display fields.
 *
 * [Source: Story R2 spec §3]
 */
export interface RenewalBoardItemDto {
  policy_id: string;
  policy_number: string;
  /** Resolved holder name: person = "Last First" | firma = name. Null if holder not found. */
  kunde: string | null;
  /** Resolved carrier name (company_master.name by carrier_uid). Null if not found. */
  versicherer: string | null;
  /** line_of_business (Sparte). Null if not set. */
  sparte: string | null;
  /** Current policy_version.premium_gross as a string (numeric(14,2)). Null if no current version. */
  praemie: string | null;
  /** policy.currency (default 'CHF'). */
  currency: string;
  /** policy.expiry_date as ISO date string. Null if not set. */
  renewal_date: string | null;
  /** renewalDate − noticePeriodDays as ISO date string. Null if not computable. */
  act_by_date: string | null;
  /** Signed days until actByDate (negative = overdue). Null if actByDate is null. */
  days_left: number | null;
  /** Time-band relative to actByDate. */
  bucket: RenewalBucket;
  /** Alert colour relative to actByDate. 'none' allowed (policy can be on board outside both windows). */
  status: RenewalThresholdStatus;
}

/**
 * Board list response DTO.
 *
 * [Source: Story R2 spec §3]
 */
export interface RenewalBoardResponseDto {
  items: RenewalBoardItemDto[];
  /** Counts grouped by bucket across the (filtered) result set. */
  bucketCounts: Record<RenewalBucket, number>;
}

/**
 * Heute / Briefing dashboard aggregation DTO.
 *
 * Directly powers the Heute block: "10 Policen werden in 30 Tagen fällig, 3 davon rot".
 *
 * [Source: Story R2 spec §3]
 */
export interface RenewalHeuteDto {
  /** Counts of all active policies, grouped by bucket. */
  bucketCounts: Record<RenewalBucket, number>;
  /** Number of policies in status 'mahnen' (the red list count). */
  redCount: number;
  /** The red items (status='mahnen'), pinned to top, sorted by days_left ASC (most urgent first). */
  redList: RenewalBoardItemDto[];
}

// ── RenewalSettings response DTO ──────────────────────────────────────────────

/**
 * RenewalSettingsDto — response shape for GET / PUT /admin/renewal-settings.
 *
 * tenant_id is NEVER present in the response (server-side only; rule #1).
 * is_default: true = no stored row; in-code default surfaced (version: null).
 * is_default: false = stored row; version is the current version number.
 *
 * [Source: Story R1 spec §5 — API surface]
 */
export interface RenewalSettingsDto {
  orange_days: number;         // N1 threshold (default 30 days) — informieren (orange)
  red_days: number;            // N2 threshold (default 7 days)  — mahnen (red)
  notice_period_days: number;  // default Kündigungsfrist (default 90 days)
  version: number | null;      // null when is_default = true
  is_default: boolean;         // true = in-code default (no stored row)
  updated_at: string | null;   // ISO 8601 timestamp string; null when is_default = true
}
